import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import Navbar from '../components/Navbar';
import RosterSection from '../components/TeamRoster/RosterSection';
import PlayerSearch from '../components/TeamRoster/PlayerSearch';
import PlayerDialog from '../components/TeamRoster/PlayerDialog';
import EvaluationLists from "@/components/TeamRoster/EvaluationLists";
import {
  Team,
  Player,
  TeamPlayer,
  CoachAgeGroup,
  PlayerStatus,
  SortField,
  SortDirection,
  AgeGroup
} from '../components/TeamRoster/types';

// Component-specific constants
const POSITIONS_OPTIONS = ['Goalkeeper', 'Defender', 'Midfielder', 'Forward'];
const COMPETITIVENESS_OPTIONS = ['Premier', 'Elite', 'Alliance'];

function TeamRoster() {
  const { teamId } = useParams<{ teamId: string }>();
  const { user } = useAuth();

  // --- Core State ---
  const [team, setTeam] = useState<Team | null>(null);
  const [teamPlayers, setTeamPlayers] = useState<TeamPlayer[]>([]);
  const [coachAgeGroups, setCoachAgeGroups] = useState<CoachAgeGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null); // General errors

  // --- Player Search State ---
  const [playerSearchTerm, setPlayerSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Player[]>([]);
  const [isSearchingPlayers, setIsSearchingPlayers] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [genderFilter, setGenderFilter] = useState('all');
  const [ageGroupFilter, setAgeGroupFilter] = useState('all');
  const [positionsFilter, setPositionsFilter] = useState<string[]>([]);
  const [competitivenessFilter, setCompetitivenessFilter] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [loadingAgeGroups, setLoadingAgeGroups] = useState(false);

  // --- Notes Modal State ---
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [currentPlayerNotes, setCurrentPlayerNotes] = useState('');
  const [currentPlayerId, setCurrentPlayerId] = useState(''); // Player ID for notes
  const [currentTeamIdForNotes, setCurrentTeamIdForNotes] = useState(''); // Team ID for notes context
  const [currentNoteId, setCurrentNoteId] = useState<string | null>(null); // Existing note ID
  const [playersWithNotes, setPlayersWithNotes] = useState<Record<string, boolean>>({}); // Track which players have notes

  // --- Data Fetching ---

  const fetchTeamDetails = useCallback(async () => {
    if (!teamId) return;
    console.log("Fetching team details for ID:", teamId);
    try {
      const { data, error: fetchError } = await supabase
        .from('teams')
        .select('*') // Adjust columns as needed, avoid '*' in production
        .eq('id', teamId)
        .single();
      if (fetchError) throw fetchError;
      setTeam(data as Team); // Add type assertion
    } catch (err: any) {
      console.error("Error fetching team details:", err);
      setError("Failed to load team details.");
      setTeam(null); // Ensure team is null on error
    }
  }, [teamId]);

  const fetchTeamPlayersAndNotesStatus = useCallback(async () => {
    if (!teamId) return;
    console.log("Fetching players and notes status for team ID:", teamId);
    try {
       // Fetch team players
      const { data: playersData, error: playersError } = await supabase
        .from('team_players')
        // Explicitly select columns for Player, including profile data
        .select(`
          id,
          status,
          player_id,
          updated_at,
          players!team_players_player_id_fkey (
            id,
            first_name,
            last_name,
            birthdate,
            sex,
            updated_at,
            profile
          )
        `)
        .eq('team_id', teamId);

      if (playersError) throw playersError;

      const fetchedTeamPlayers = (playersData || []).map((tp) => ({
        id: tp.id,
        player_id: tp.player_id,
        team_id: teamId,
        status: tp.status as PlayerStatus,
        updated_at: tp.updated_at,
        player: tp.players as Player // Assert Player type
      }));
      setTeamPlayers(fetchedTeamPlayers);

       // Fetch distinct player IDs who have notes for this team
      const { data: notesStatusData, error: notesStatusError } = await supabase
        .from('team_player_notes')
        .select('player_id')
        .eq('team_id', teamId)
        .neq('notes', '') // Optionally only count if notes are not empty

      if (notesStatusError) throw notesStatusError;

      const notesMap: Record<string, boolean> = {};
      (notesStatusData || []).forEach(note => {
          notesMap[note.player_id] = true;
      });
      setPlayersWithNotes(notesMap);

    } catch (err: any) {
      console.error("Error fetching team players or notes status:", err);
      setError("Failed to load team players or check notes.");
       setTeamPlayers([]); // Reset on error
       setPlayersWithNotes({}); // Reset on error
    }
  }, [teamId]);


  const fetchCoachAgeGroups = useCallback(async () => {
    if (!user) return;
    setLoadingAgeGroups(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('coach_age_groups')
        .select(`
          id,
          coach_id,
          age_group_id,
          age_group:age_groups(*)
        `)
        .eq('coach_id', user.id);

      if (fetchError) throw fetchError;

      const typedData = (data || []).map(item => ({
        id: item.id,
        coach_id: item.coach_id,
        age_group_id: item.age_group_id,
        age_group: item.age_group as AgeGroup // Assert AgeGroup type
      }));
      setCoachAgeGroups(typedData);
    } catch (err: any) {
      console.error('Error fetching coach age groups:', err);
      // Non-critical error, maybe set a specific state?
    } finally {
      setLoadingAgeGroups(false);
    }
  }, [user]);

  // --- Player Search Logic ---

  const performSearch = useCallback(async (term: string) => {
    setIsSearchingPlayers(true);
    setError(null); // Clear previous errors related to search
    try {
      let query = supabase
        .from('players')
        .select(`
          id, 
          first_name, 
          last_name, 
          birthdate, 
          sex, 
          updated_at,
          age_group_id,
          age_group:age_groups(*),
          profile
        `); // Fetch necessary fields including profile
      
      if (coachAgeGroups.length > 0) {
        console.log('Coach Age Groups:', coachAgeGroups);
        query = query.in('age_group_id', coachAgeGroups.map(ag => ag.age_group_id));
      }

      if (term.trim()) {
        query = query.or(`first_name.ilike.%${term}%,last_name.ilike.%${term}%`);
      }
      if (genderFilter !== 'all') {
        query = query.eq('sex', genderFilter);
      }
      if (ageGroupFilter !== 'all') {
        query = query.eq('age_group_id', ageGroupFilter);
      }

      const { data: searchData, error: searchError } = await query;
      if (searchError) throw searchError;

      // Instead of filtering out players on this team, we'll keep them all and annotate with team status
      let allPlayers = searchData || [];
      
      // First, get all team associations for players in search results
      const { data: allTeamPlayers, error: teamPlayersError } = await supabase
        .from('team_players')
        .select(`
          id, 
          team_id, 
          player_id, 
          status,
          teams:teams(id, name)
        `)
        .in('player_id', allPlayers.map(p => p.id));
        
      if (teamPlayersError) throw teamPlayersError;
      
      // Create a map of player_id -> array of team statuses
      const playerTeamStatusMap: Record<string, any[]> = {};
      allTeamPlayers?.forEach(tp => {
        if (!playerTeamStatusMap[tp.player_id]) {
          playerTeamStatusMap[tp.player_id] = [];
        }
        // Access the name from tp.teams which should be an object with properties
        const teamName = tp.teams && typeof tp.teams === 'object' ? (tp.teams as any).name : 'Unknown Team';
        playerTeamStatusMap[tp.player_id].push({
          teamId: tp.team_id,
          teamName: teamName,
          status: tp.status,
          currentTeam: tp.team_id === teamId
        });
      });
      
      // Apply playerTeamStatusMap to search results
      let processedResults = allPlayers.map((p: any) => ({
        ...p,
        teamStatus: playerTeamStatusMap[p.id] || []
      }));

      // Client-side filtering for complex criteria (like array contains)
       if (positionsFilter.length > 0) {
         processedResults = processedResults.filter(player =>
           player.profile?.positions && positionsFilter.some(pos => player.profile?.positions?.includes(pos))
         );
       }
       if (competitivenessFilter.length > 0) {
         processedResults = processedResults.filter(player =>
           player.profile?.competitiveness && competitivenessFilter.some(comp => player.profile?.competitiveness?.includes(comp))
         );
       }

      // Filter out players with age groups not assigned to this coach via coach_age_groups table
      // processedResults = processedResults.filter(player =>
      //   coachAgeGroups.some(coachAgeGroup =>
      //     coachAgeGroup.age_group_id === player.age_group_id
      //   )
      // );

      setSearchResults(processedResults as Player[]); // Assert Player type
    } catch (err: any) {
      console.error("Player search error:", err);
      setError("Failed to search for players.");
      setSearchResults([]);
    } finally {
      setIsSearchingPlayers(false);
    }
  }, [teamPlayers, genderFilter, ageGroupFilter, positionsFilter, competitivenessFilter, sortField, sortDirection]);

  const handleSearch = useCallback(() => {
    performSearch(playerSearchTerm);
  }, [playerSearchTerm, performSearch]);

  // --- Filter Handlers ---

  const clearFilters = () => {
    setGenderFilter('all');
    setAgeGroupFilter('all');
    setPositionsFilter([]);
    setCompetitivenessFilter([]);
    // Optionally trigger search immediately or wait for user action
    // handleSearch(); // Uncomment to search on clear
  };

  const handlePositionFilterChange = (isChecked: boolean | 'indeterminate', position: string) => {
    if (typeof isChecked === 'boolean') {
        setPositionsFilter(prev =>
            isChecked ? [...prev, position] : prev.filter(p => p !== position)
        );
    }
  };

  const handleCompetitivenessFilterChange = (isChecked: boolean | 'indeterminate', level: string) => {
     if (typeof isChecked === 'boolean') {
        setCompetitivenessFilter(prev =>
            isChecked ? [...prev, level] : prev.filter(c => c !== level)
        );
    }
  };

  // --- Team Player Actions ---

  const handleAddPlayerToConsideration = async (player: any) => {
    if (!teamId) return;
    setError(null);
    try {
      const { error: insertError } = await supabase
        .from('team_players')
        .insert({ team_id: teamId, player_id: player.id, status: 'considering' });

      if (insertError) {
        if (insertError.code === '23505') { // Unique violation
          setError(`Player ${player.first_name} ${player.last_name} is already associated with this team.`);
        } else {
          throw insertError;
        }
      } else {
        await fetchTeamPlayersAndNotesStatus(); // Refresh list and notes status
        setSearchResults(prevResults => prevResults.filter(p => p.id !== player.id)); // Remove from search results
      }
    } catch (err: any) {
      console.error("Error adding player to consideration:", err);
      setError(`Failed to add player ${player.first_name} ${player.last_name}.`);
    }
  };

  const updateTeamPlayerStatus = async (teamPlayerId: string, playerId: string, newStatus: PlayerStatus) => {
    if (!teamId) return;
    setError(null);
    try {
      const { error: updateError } = await supabase
        .from('team_players')
        .update({ status: newStatus })
        .eq('id', teamPlayerId);

      if (updateError) throw updateError;

      //console.log(`Player ${playerId} status updated to ${newStatus} for team ${teamId}`);
      await fetchTeamPlayersAndNotesStatus(); // Refresh list and notes status
    } catch (err: any) {
      console.error(`Error updating player ${playerId} status to ${newStatus}:`, err);
      setError(`Failed to update player status.`);
    }
  };

  // Specific status update handlers
  const handleOfferSpot = (teamPlayerId: string, playerId: string) => updateTeamPlayerStatus(teamPlayerId, playerId, 'offered');
  const handleRescindOffer = (teamPlayerId: string, playerId: string) => updateTeamPlayerStatus(teamPlayerId, playerId, 'considering');
  const handleAcceptOffer = (teamPlayerId: string, playerId: string) => updateTeamPlayerStatus(teamPlayerId, playerId, 'rostered');
  const handleRemoveFromRoster = (teamPlayerId: string, playerId: string) => updateTeamPlayerStatus(teamPlayerId, playerId, 'considering');
  const handleDeclineOffer = (teamPlayerId: string, playerId: string) => updateTeamPlayerStatus(teamPlayerId, playerId, 'declined');

  const handleRemovePlayer = async (teamPlayerId: string, playerId: string) => {
    if (!teamId) return;
    setError(null);
    // Optional: Add confirmation dialog here
    try {
      // Consider deleting associated notes first if required by constraints or logic
      // const { error: deleteNotesError } = await supabase ... where player_id and team_id match

      const { error: deleteError } = await supabase
        .from('team_players')
        .delete()
        .eq('id', teamPlayerId);

      if (deleteError) throw deleteError;

      console.log(`Player ${playerId} removed from team ${teamId}`);
       await fetchTeamPlayersAndNotesStatus(); // Refresh list and notes status
    } catch (err: any) {
      console.error("Error removing player:", err);
      setError("Failed to remove player.");
    }
  };

  // --- Notes Management ---

  const handleOpenNotes = async (teamPlayerId: string, playerId: string) => {
    setCurrentPlayerId(playerId);
    setError(null);
    try {
      const teamPlayerRecord = teamPlayers.find(tp => tp.id === teamPlayerId);
      if (!teamPlayerRecord || !teamPlayerRecord.team_id) {
        throw new Error("Team player record or team ID not found");
      }
      setCurrentTeamIdForNotes(teamPlayerRecord.team_id); // Set team context for save

      // Fetch the single note for this player/team
      const { data: notesData, error: notesError } = await supabase
        .from('team_player_notes')
        .select('*')
        .eq('team_id', teamPlayerRecord.team_id)
        .eq('player_id', playerId)
        .order('created_at', { ascending: false })
        .limit(1); // Fetch the latest note

      if (notesError) throw notesError;

      if (notesData && notesData.length > 0) {
          const latestNote = notesData[0];
          setCurrentPlayerNotes(latestNote.notes || "");
          setCurrentNoteId(latestNote.id);
      } else {
          setCurrentPlayerNotes("");
          setCurrentNoteId(null);
      }
      setIsNotesModalOpen(true);
    } catch (err: any) {
      console.error("Error fetching player notes:", err);
      setError("Failed to fetch player notes.");
    }
  };

  const handleSaveNotes = async () => {
    if (!currentPlayerId || !currentTeamIdForNotes) return;
    setError(null);
    try {
      const noteData = {
          team_id: currentTeamIdForNotes,
          player_id: currentPlayerId,
          notes: currentPlayerNotes,
          note_type: 'internal_team',
      };

      let upsertError = null;
      if (currentNoteId) {
        // Update existing note
        const { error } = await supabase
          .from('team_player_notes')
          .update(noteData)
          .eq('id', currentNoteId);
        upsertError = error;
      } else {
        // Insert new note
        const { error } = await supabase
          .from('team_player_notes')
          .insert(noteData);
         upsertError = error;
         // Optionally fetch the new note ID if needed immediately after insert
      }

      if (upsertError) throw upsertError;

      console.log(`Notes saved for player ${currentPlayerId} on team ${currentTeamIdForNotes}`);
      // Update UI state immediately
      setPlayersWithNotes(prev => ({
          ...prev,
          [currentPlayerId]: currentPlayerNotes.trim().length > 0 // Update based on whether notes exist now
      }));
      setIsNotesModalOpen(false);
      // No full refresh needed unless note content display changes elsewhere

    } catch (err: any) {
      console.error("Error saving player notes:", err);
      setError("Failed to save player notes.");
    }
  };

  // --- Effects ---
  useEffect(() => {
    // Initial load: Fetch team details, players, and coach age groups
    if (teamId && user) {
      setLoading(true);
      setError(null);
      Promise.all([
        fetchTeamDetails(),
        fetchTeamPlayersAndNotesStatus(),
        fetchCoachAgeGroups()
      ]).catch((err) => {
        console.error("Error during initial data fetch:", err);
        // Error state is set within individual fetch functions
      }).finally(() => {
        setLoading(false);
      });
    } else if (!teamId) {
        setError("Team ID is missing.");
        setLoading(false);
    } else if (!user) {
        // Handle case where user is not yet available? Maybe wait or show login prompt.
        setLoading(false); // Or keep loading until user is available
    }
  }, [teamId, user, fetchTeamDetails, fetchTeamPlayersAndNotesStatus, fetchCoachAgeGroups]); // Dependencies for initial load


  // --- Render Logic ---

  if (loading) {
    return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
            <div className="w-8 h-8 border-b-2 border-indigo-600 rounded-full animate-spin"></div>
        </div>
    );
  }

  // Display critical loading error or missing team
  if (!loading && (!team || (error && !team))) { // Show error if fetching failed OR if team is null after loading
      return (
          <div className="min-h-screen bg-gray-100">
              <Navbar />
              <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                  <div className="p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50" role="alert">
                      <span className="font-medium">Error:</span> {error || "Team could not be loaded."}
                  </div>
              </main>
          </div>
      );
  }

  // Filter players by status for rendering sections
  const consideringPlayers = teamPlayers.filter(tp => tp.status === 'considering');
  const offeredPlayers = teamPlayers.filter(tp => tp.status === 'offered');
  const rosteredPlayers = teamPlayers.filter(tp => tp.status === 'rostered');
  const declinedPlayers = teamPlayers.filter(tp => tp.status === 'declined');

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Team Name is guaranteed here due to the check above */}
        <h1 className="text-2xl font-bold mb-6">{team!.name}</h1>

        {/* Display non-critical errors (e.g., failed note save) */}
        {error && (
          <div className="p-4 mb-4 text-sm text-yellow-800 rounded-lg bg-yellow-50" role="alert">
             <span className="font-medium">Notice:</span> {error}
          </div>
        )}

        <Tabs defaultValue="roster" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="roster">Roster Management</TabsTrigger>
            <TabsTrigger value="player-search">Player Search</TabsTrigger>
            <TabsTrigger value="evaluation">Evaluation Lists</TabsTrigger>
          </TabsList>

          {/* --- Roster Management Tab --- */}
          <TabsContent value="roster" className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              <RosterSection
                title="Under Consideration"
                players={consideringPlayers}
                status="considering"
                onOfferSpot={handleOfferSpot}
                onRemovePlayer={handleRemovePlayer}
                onOpenNotes={handleOpenNotes}
                playersWithNotes={playersWithNotes}
              />
              <RosterSection
                title="Offered"
                players={offeredPlayers}
                status="offered"
                onAcceptOffer={handleAcceptOffer}
                onDeclineOffer={handleDeclineOffer}
                onRescindOffer={handleRescindOffer}
                onOpenNotes={handleOpenNotes}
                playersWithNotes={playersWithNotes}
              />
              <RosterSection
                title="Final Roster"
                players={rosteredPlayers}
                status="rostered"
                onRemoveFromRoster={handleRemoveFromRoster}
                onOpenNotes={handleOpenNotes}
                playersWithNotes={playersWithNotes}
              />
              <RosterSection
                title="Declined"
                players={declinedPlayers}
                status="declined"
                onOpenNotes={handleOpenNotes}
                onRescindOffer={handleRescindOffer}
                playersWithNotes={playersWithNotes}
              />
            </div>
          </TabsContent>

          {/* --- Player Search Tab --- */}
          <TabsContent value="player-search" className="space-y-6">
             <PlayerSearch
                playerSearchTerm={playerSearchTerm}
                setPlayerSearchTerm={setPlayerSearchTerm}
                handleSearch={handleSearch}
                isSearchingPlayers={isSearchingPlayers}
                searchResults={searchResults}
                handleAddPlayerToConsideration={handleAddPlayerToConsideration}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                genderFilter={genderFilter}
                setGenderFilter={setGenderFilter}
                ageGroupFilter={ageGroupFilter}
                setAgeGroupFilter={setAgeGroupFilter}
                positionsFilter={positionsFilter}
                handlePositionFilterChange={handlePositionFilterChange}
                competitivenessFilter={competitivenessFilter}
                handleCompetitivenessFilterChange={handleCompetitivenessFilterChange}
                coachAgeGroups={coachAgeGroups}
                loadingAgeGroups={loadingAgeGroups}
                positionsOptions={POSITIONS_OPTIONS}
                competitivenessOptions={COMPETITIVENESS_OPTIONS}
                clearFilters={clearFilters}
                currentTeamId={team ? team.id : ''}
            />
          </TabsContent>

          {/* --- Evaluation Lists Tab --- */}
          <TabsContent value="evaluation" className="space-y-6">
            <EvaluationLists onAddPlayer={handleAddPlayerToConsideration} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Notes Modal */}
      <PlayerDialog
        isOpen={isNotesModalOpen}
        onOpenChange={setIsNotesModalOpen}
        currentPlayerId={currentPlayerId}
        currentPlayerNotes={currentPlayerNotes}
        setCurrentPlayerNotes={setCurrentPlayerNotes}
        onSave={handleSaveNotes}
        playerName={(() => {
          const teamPlayer = teamPlayers.find(tp => tp.player_id === currentPlayerId || tp.id === currentPlayerId);
          return teamPlayer && teamPlayer.player ? `${teamPlayer.player.first_name ?? ''} ${teamPlayer.player.last_name ?? ''}`.trim() : '';
        })()}
        currentTeamId={team ? team.id : ''}
      />
    </div>
  );
}

export default TeamRoster;